/**
 * 회기 처리의 전사 단계 (TRANSCRIPT_PROTOCOL §3, MASTER §6.2) + 검증 + 발췌 추천.
 *
 * - 원문(RAW)에서 결정적으로 다시 계산할 수 있으므로, 중간에 멈춰도 같은 결과로 이어서 만든다.
 * - 저장 순서: 불일치 기록 → Working Transcript → 회기 상태. 각 기록은 결정적 id라 재실행해도 중복이 없다.
 * - 화자 판정은 사용자에게 묻지 않는다. 사용자가 전사를 직접 열어 고친 경우만 roleSource = user.
 */
import type { Session, Transcript, TranscriptSegment, VerbatimSelection } from '@/domain/schema/v1';
import { runPipeline, type PipelineSegment } from '@/domain/transcript/pipeline';
import { recommendExchanges, recommendVerbatims, type ExcerptCandidate } from '@/domain/transcript/excerpts';
import { getBlobText } from '@/data/blobs';
import type { RepoContext, Repository } from '@/data/repo/repository';

/** 기본 용어 사전 (사용자가 GlossaryTerm으로 추가·수정할 수 있다) */
export const DEFAULT_GLOSSARY = [
  '성령님',
  '하나님',
  '묵상',
  '관상 기도',
  '경외',
  '경청',
  '자동적 사고',
  '사례개념화',
  '공감',
];

/** seed로부터 항상 같은 UUIDv7 (재실행 멱등용) */
export async function stableId(seed: string, timeMs: number): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed)));
  const hex = (b: Uint8Array) => [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  const ts = timeMs.toString(16).padStart(12, '0').slice(-12);
  const r = hex(h);
  const variant = ((parseInt(r[3]!, 16) & 0x3) | 0x8).toString(16);
  return `${ts.slice(0, 8)}-${ts.slice(8, 12)}-7${r.slice(0, 3)}-${variant}${r.slice(4, 7)}-${r.slice(7, 19)}`;
}

const priorityOf = (level: number) =>
  (level >= 4 ? 'critical' : level === 3 ? 'high' : level === 2 ? 'medium' : 'low') as
    'critical' | 'high' | 'medium' | 'low';

export class TranscriptService {
  constructor(
    private readonly repo: Repository,
    private readonly ctx: RepoContext,
  ) {}

  private async raw(id: string | undefined): Promise<{ t: Transcript; text: string } | null> {
    if (!id) return null;
    const t = await this.repo.find('Transcript', id);
    if (!t?.rawBlobId) return null;
    return { t, text: await getBlobText(this.ctx, t.rawBlobId) };
  }

  async glossary(): Promise<string[]> {
    const user = (await this.repo.list('GlossaryTerm')).map((g) => g.term);
    return [...new Set([...user, ...DEFAULT_GLOSSARY])];
  }

  /** Working Transcript 만들기 (이미 있으면 그대로). @returns 확인이 필요한 개수 */
  async buildWorking(session: Session): Promise<{ working: Transcript; needsReview: number }> {
    const existing = session.workingTranscriptId
      ? await this.repo.find('Transcript', session.workingTranscriptId)
      : null;
    if (existing) return { working: existing, needsReview: existing.stats?.needsReview ?? 0 };

    const [g, i] = await Promise.all([this.raw(session.samsungTranscriptId), this.raw(session.appleTranscriptId)]);
    const result = runPipeline({
      galaxyText: g?.text ?? '',
      iphoneText: i?.text ?? '',
      glossary: await this.glossary(),
    });
    const baseMs = Date.parse(session.createdAt);
    const workingId = await stableId(`working|${session.id}`, baseMs);

    // 1) 불일치 기록 (Level 2 이상)
    const discIds = new Map<string, string>();
    for (const s of result.segments) {
      if (s.discrepancyLevel === undefined || s.discrepancyLevel < 2 || !s.iphoneText || !s.galaxyText) continue;
      const id = await stableId(`disc|${session.id}|${s.segmentId}`, baseMs);
      discIds.set(s.segmentId, id);
      await this.repo.create(
        'TranscriptDiscrepancy',
        {
          clientId: session.clientId,
          sessionId: session.id,
          samsungSegmentIds: [s.segmentId],
          appleSegmentIds: [s.segmentId],
          samsungText: s.galaxyText,
          appleText: s.iphoneText,
          ...(s.matchScore !== undefined ? { normalizedSimilarity: s.matchScore } : {}),
          level: s.discrepancyLevel,
          category: s.discrepancyCategory ?? 'unknown',
          priority: priorityOf(s.discrepancyLevel),
          resolution: 'unresolved',
        },
        'system',
        { id },
      );
    }

    // 2) Working Transcript
    const segments: TranscriptSegment[] = result.segments.map((s) =>
      this.toSegment(s, g?.t.id, i?.t.id, discIds.get(s.segmentId)),
    );
    const working = await this.repo.create(
      'Transcript',
      {
        clientId: session.clientId,
        sessionId: session.id,
        source: 'working',
        language: 'ko',
        parserVersion: 'parse-0.1',
        parseStatus: g || i ? 'parsed' : 'failed',
        parseWarnings: result.stats.warnings,
        stats: {
          galaxyTurns: result.stats.galaxyTurns,
          iphoneBlocks: result.stats.iphoneBlocks,
          aligned: result.stats.aligned,
          offsetMs: result.stats.offsetMs,
          needsReview: result.stats.needsReview,
          critical: result.stats.critical,
        },
        segments,
      },
      'system',
      { id: workingId },
    );
    return { working, needsReview: result.stats.needsReview };
  }

  private toSegment(s: PipelineSegment, galaxyId?: string, iphoneId?: string, discId?: string): TranscriptSegment {
    return {
      segmentId: s.segmentId,
      startMs: Math.max(0, Math.round(s.startMs)),
      ...(s.galaxyTimeMs !== undefined ? { galaxyTimeMs: s.galaxyTimeMs } : {}),
      ...(s.iphoneTimeRange
        ? {
            iphoneTimeRange: {
              startMs: Math.max(0, s.iphoneTimeRange.startMs),
              endMs: Math.max(0, s.iphoneTimeRange.endMs),
            },
          }
        : {}),
      ...(s.sourceSpeakerLabel ? { sourceSpeakerLabel: s.sourceSpeakerLabel } : {}),
      clinicalRole: s.clinicalRole,
      roleConfidence: s.roleConfidence,
      roleSource: 'auto',
      roleModelVersion: 'rules-hmm-0.1',
      ...(s.counselorAct ? { counselorAct: s.counselorAct } : {}),
      isBackchannel: s.isBackchannel,
      ...(s.localCorrection ? { localCorrection: true } : {}),
      ...(s.splitFrom ? { splitFrom: s.splitFrom } : {}),
      text: s.text,
      textConfidence: s.textConfidence,
      ...(s.matchScore !== undefined ? { matchScore: s.matchScore } : {}),
      ...(galaxyId && s.galaxyLines
        ? {
            samsungRawRefs: [
              {
                transcriptId: galaxyId,
                lineStart: s.galaxyLines.lineStart,
                lineEnd: Math.max(s.galaxyLines.lineStart, s.galaxyLines.lineEnd),
                charStart: s.galaxyLines.charStart,
                charEnd: s.galaxyLines.charEnd,
              },
            ],
          }
        : {}),
      ...(iphoneId && s.iphoneLines?.length
        ? {
            iphoneRawRefs: s.iphoneLines.map((l) => ({
              transcriptId: iphoneId,
              lineStart: l.lineStart,
              lineEnd: Math.max(l.lineStart, l.lineEnd),
            })),
          }
        : {}),
      ...(s.discrepancyLevel !== undefined ? { discrepancyLevel: s.discrepancyLevel } : {}),
      verificationStatus: s.verificationStatus,
      ...(s.clinicalPriority ? { clinicalPriority: s.clinicalPriority } : {}),
      ...(discId ? { discrepancyIds: [discId] } : {}),
      ...(s.weakAgreement ? { weakAgreement: true } : {}),
      ...(s.glossaryCorrected?.length ? { glossaryCorrected: s.glossaryCorrected } : {}),
    };
  }

  async getWorking(session: Session): Promise<Transcript | null> {
    return session.workingTranscriptId ? this.repo.find('Transcript', session.workingTranscriptId) : null;
  }

  // ---------------- 검증 ----------------

  async reviewItems(session: Session) {
    const w = await this.getWorking(session);
    if (!w) return [];
    const discs = new Map(
      (await this.repo.list('TranscriptDiscrepancy', { sessionId: session.id })).map((d) => [d.id, d]),
    );
    return w.segments
      .filter((s) => s.verificationStatus === 'needs-review')
      .map((s) => ({ segment: s, discrepancy: s.discrepancyIds?.[0] ? discs.get(s.discrepancyIds[0]) : undefined }));
  }

  private async updateSegment(session: Session, segmentId: string, patch: (s: TranscriptSegment) => TranscriptSegment) {
    const w = await this.getWorking(session);
    if (!w) throw new Error('Working Transcript가 없습니다');
    const segments = w.segments.map((s) => (s.segmentId === segmentId ? patch(s) : s));
    return this.repo.update('Transcript', w.id, { segments }, 'user', { expectedRevision: w.revision });
  }

  /** 확인 카드 처리: 삼성/Apple/직접 수정/중요하지 않음 */
  async resolveReview(
    session: Session,
    segmentId: string,
    choice: 'samsung' | 'apple' | 'manual' | 'ignored',
    manualText?: string,
  ) {
    const items = await this.reviewItems(session);
    const item = items.find((x) => x.segment.segmentId === segmentId);
    const d = item?.discrepancy;
    const text =
      choice === 'samsung'
        ? (d?.samsungText ?? item?.segment.text)
        : choice === 'apple'
          ? (d?.appleText ?? item?.segment.text)
          : choice === 'manual'
            ? manualText
            : undefined;
    await this.updateSegment(session, segmentId, (s) => ({
      ...s,
      ...(text ? { text } : {}),
      verificationStatus: choice === 'ignored' ? 'ignored' : 'verified',
      ...(choice !== 'ignored' ? { textConfidence: 1 } : {}),
    }));
    if (d) {
      await this.repo.update(
        'TranscriptDiscrepancy',
        d.id,
        {
          resolution: choice,
          ...(text ? { resolvedText: text } : {}),
          resolvedBy: 'user',
          resolvedAt: new Date().toISOString(),
        },
        'user',
      );
    }
  }

  /** 사용자가 전사 화면에서 직접 역할을 고친 경우 (routine 확인 아님) */
  async setRole(session: Session, segmentId: string, role: 'counselor' | 'client' | 'unknown') {
    await this.updateSegment(session, segmentId, (s) => ({
      ...s,
      clinicalRole: role,
      roleConfidence: role === 'unknown' ? 0.5 : 1,
      roleSource: 'user',
    }));
  }

  // ---------------- 발췌·축어록 ----------------

  async recommend(
    session: Session,
    hypotheses: string[],
  ): Promise<{ exchanges: ExcerptCandidate[]; verbatims: ExcerptCandidate[] }> {
    const w = await this.getWorking(session);
    if (!w) return { exchanges: [], verbatims: [] };
    const segs = w.segments.map((s) => ({ ...s, startMs: s.startMs ?? 0 }));
    return {
      exchanges: recommendExchanges(segs, { hypotheses }),
      verbatims: recommendVerbatims(segs, { hypotheses, minutes: 12 }),
    };
  }

  async saveSelection(session: Session, c: ExcerptCandidate): Promise<VerbatimSelection> {
    return this.repo.create(
      'VerbatimSelection',
      {
        clientId: session.clientId,
        sessionId: session.id,
        startMs: Math.max(0, Math.round(c.startMs)),
        endMs: Math.max(0, Math.round(c.endMs)),
        segmentIdsSnapshot: c.segmentIds,
        selectionReason: c.reasons.join(' · '),
        supervisionQuestions: c.supervisionQuestions,
        processNotes: c.kind === 'exchange' ? '주요 교환 발췌' : '축어록',
        proposedBy: 'user',
        status: 'selected',
      },
      'user',
    );
  }

  /** 직접 구간 지정 (예: 12:00~25:00). 맞장구 포함 그 구간의 모든 줄을 그대로 */
  async saveRange(session: Session, startMs: number, endMs: number, reason?: string): Promise<VerbatimSelection> {
    if (!(endMs > startMs)) throw new Error('끝 시각이 시작 시각보다 뒤여야 합니다');
    const w = await this.getWorking(session);
    const segs = (w?.segments ?? []).filter((s) => (s.startMs ?? 0) >= startMs && (s.startMs ?? 0) < endMs);
    if (!segs.length) throw new Error('그 구간에 전사 줄이 없습니다');
    return this.repo.create(
      'VerbatimSelection',
      {
        clientId: session.clientId,
        sessionId: session.id,
        startMs: Math.max(0, Math.round(startMs)),
        endMs: Math.max(0, Math.round(endMs)),
        segmentIdsSnapshot: segs.map((s) => s.segmentId),
        ...(reason?.trim() ? { selectionReason: reason.trim() } : {}),
        processNotes: '축어록',
        proposedBy: 'user',
        status: 'selected',
      },
      'user',
    );
  }

  /** AI가 추천한 구간을 상담자가 채택 */
  adoptCandidate(id: string) {
    return this.repo.update('VerbatimSelection', id, { status: 'selected' }, 'user');
  }

  listSelections(filter: { clientId?: string; sessionId?: string }) {
    return this.repo.list('VerbatimSelection', filter);
  }

  removeSelection(id: string) {
    return this.repo.softDelete('VerbatimSelection', id);
  }
}
